import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Truncate from 'react-truncate';

import DataFlag from '../data-flag';
import { cn, superScriptSanitizer, get, getText } from '../../helpers';

import Image from '../_common/image';
import { ReactComponent as SolutionIcon } from '../../assets/svg/icons/solution-icon.svg';
import { ReactComponent as BookmarkIcon } from '../../assets/svg/icons/bookmark.svg';

const moveThreshold = 10;

const langCode = getText('langCode');

export default function Tile(props: TileProps) {
    const { provider, name, description, imgUrl = '', url, buttonText, className, addedDate, enhancementDate, icon, tileType, id } = props;
    const isExternalLink = url.match(/^(ftp|http|https):\/\/[^ "]+$/g);
    const [pos, setPos] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const { isLoggedIn, bookmarkPreference } = useSelector((state: RootStoreState) => get(state, 'user'));
    let bookmark: Bookmarks = { datasets: [], solutions: [], queries: [], blueprints: [] };
    if (bookmarkPreference) bookmark = bookmarkPreference.bookMarks || { datasets: [], solutions: [], queries: [], blueprints: [] };
    let isBookmarked = false;
    if (tileType === 'Solutions') {
        isBookmarked = bookmark.solutions && bookmark.solutions.some(x => x.keyId === id);
    } else if (tileType === 'Blueprint') {
        isBookmarked = bookmark.blueprints && bookmark.blueprints.some(x => x.keyId === id);
    } else {
        isBookmarked = bookmark.datasets && bookmark.datasets.some(x => x.keyId.toString() === id.toString());
    }
    const fileName = imgUrl.match(/[^/]+$/g) || '';
    const fileNameSmall = (fileName[0] && fileName[0].toString().replace('.', '_small.')) || '';
    const srcset = {
        '345w': fileNameSmall ? imgUrl.replace(fileName[0].toString(), fileNameSmall).toString() : '',
        '720w': imgUrl ? imgUrl.toString() : ''
    };

    // custom logic for text truncate and ellipsis as react-truncate does not work with spaceless langs like chinese, japanese and korean.
    const truncateText = (text: string) => {
        if (langCode === 'en') {
            return <span>{ text.length > 175 ? `${text.substring(0, 175)}...` : `${text}`}</span>;
        }
        if (text.length > 100) {
            return <span>{`${text.substring(0, 100)}...`}</span>;
        }
        return <span>{text}</span>;
    };

    const truncateTitle = (text: string) => {
        if (text.length > 56) {
            return `${text.substring(0, 53)}...`;
        }
        return text;
    };

    const getLinkContent = () => {
        return (
            <article className="tile__inner">
                {icon && <div className="tile__icon">{icon}</div>}
                <div className="tile__content">
                    <DataFlag datasetAddedDate={addedDate} datasetEnhancementDate={enhancementDate} isSmall={false} />
                    <span className="data-icon">
                        {isLoggedIn && isBookmarked && <BookmarkIcon className="data-icon__tile-bookmark" />}
                        {(tileType === 'Solutions' && <SolutionIcon className="data-icon__tile-icon" />)}
                    </span>
                    <h1 className="tile__title" data-testid="tile-title">
                        {superScriptSanitizer(truncateTitle(name))}
                    </h1>
                    {(tileType === 'DatasetBundles') ? <p className="tile__bundle">{getText('generic.datasetBundles')}</p> : ''}
                    <div className="tile__body">
                        <p className="tile__sector">
                            <span>
                                {provider}
                                {/* <Truncate lines={1}>{provider}</Truncate> */}
                            </span>
                        </p>
                        <p className="tile__description">{truncateText(description)}</p>
                    </div>
                    <footer className="tile__footer">
                        <span className="button button--secondary button--fullwidth">{buttonText}</span>
                    </footer>
                </div>
                <div className="tile__bg">
                    <div className="image-gradient">
                        <Image
                            src={imgUrl}
                            sources={srcset}
                            sizes="(min-width: 2560px) 12vw,(min-width: 1450px) 18vw,(min-width: 1080px) 24vw,(max-width: 480px) 80vw, 100vw"
                            alt=""
                            className="tile__bg-image"
                            data-testid="tile-bg-img"
                        />
                    </div>
                </div>
            </article>
        );
    };

    if (isExternalLink) {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn('tile', className)}
                onMouseDown={(event: React.MouseEvent<HTMLAnchorElement>) => {
                    event.preventDefault();
                    setPos({ x: event.clientX, y: event.clientY });
                }}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                    if (Math.abs(event.clientX - pos.x) > moveThreshold || Math.abs(event.clientY - pos.y) > moveThreshold) {
                        event.preventDefault();
                    }
                }}
                data-testid="tile-link"
            >
                {getLinkContent()}
            </a>
        );
    }

    return (
        <Link
            to={url}
            className={cn('tile', className)}
            onMouseDown={(event: React.MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                setPos({ x: event.clientX, y: event.clientY });
            }}
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                if (Math.abs(event.clientX - pos.x) > moveThreshold || Math.abs(event.clientY - pos.y) > moveThreshold) {
                    event.preventDefault();
                }
            }}
            data-testid="tile-link"
        >
            {getLinkContent()}
        </Link>
    );
}
